import { useCallback, useEffect, useState } from 'react';

export type LayoutWidthMode = 'full' | 'comfortable';

const STORAGE_KEY = 'layout_width_mode';
const EVENT_NAME = 'layoutWidthModeChanged';

function normalize(value: string | null | undefined): LayoutWidthMode {
  // Default is 'full' — wide monitors should use the whole screen; the
  // 'comfortable' mode restores the legacy per-page max-width caps.
  return value === 'comfortable' ? 'comfortable' : 'full';
}

export function getStoredLayoutWidthMode(): LayoutWidthMode {
  try {
    return normalize(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'full';
  }
}

export function setStoredLayoutWidthMode(mode: LayoutWidthMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {}
  try {
    // Same-tab reactivity: the 'storage' event only fires in OTHER tabs,
    // so broadcast a CustomEvent for currently-mounted consumers here
    // (same pattern as useLocationSelection's 'locationSelected' event).
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { mode } }));
  } catch {}
}

export function useLayoutWidth(): { mode: LayoutWidthMode; setMode: (mode: LayoutWidthMode) => void } {
  const [mode, setModeState] = useState<LayoutWidthMode>(() => {
    if (typeof window === 'undefined') return 'full';
    return getStoredLayoutWidthMode();
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setModeState(normalize(e.newValue));
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && 'mode' in detail) setModeState(normalize(detail.mode));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
    };
  }, []);

  const setMode = useCallback((m: LayoutWidthMode) => {
    setModeState(m);
    setStoredLayoutWidthMode(m);
  }, []);

  return { mode, setMode };
}

/**
 * Page-shell max-width helper. Returns undefined (no cap — fill the screen)
 * in 'full' mode, or the page's legacy cap in 'comfortable' mode.
 * Pages keep their own padding and `margin: '0 auto'` so comfortable mode
 * still centers content.
 */
export function usePageMaxWidth(comfortableCap: number | string): string | number | undefined {
  const { mode } = useLayoutWidth();
  return mode === 'full' ? undefined : comfortableCap;
}
